import { useCallback, useEffect, useState } from "react";

import type { BodyWeightDataPoint } from "~/api/bodyWeight";
import { getBodyWeightData, getDateRange } from "~/api/bodyWeight";

interface UseBodyWeightDataProps {
  patientId: number;
  timeRange: string;
  navigationOffset?: number;
  selectedDate?: Date;
}

interface UseBodyWeightDataReturn {
  data: BodyWeightDataPoint[];
  metadata: {
    min: number;
    max: number;
    avg: number;
  } | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useBodyWeightData = ({
  patientId,
  timeRange,
  navigationOffset = 0,
  selectedDate,
}: UseBodyWeightDataProps): UseBodyWeightDataReturn => {
  const [data, setData] = useState<BodyWeightDataPoint[]>([]);
  const [metadata, setMetadata] = useState<{
    min: number;
    max: number;
    avg: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const dateRange = getDateRange(timeRange, navigationOffset, selectedDate);


      const response = await getBodyWeightData({
        patient_id: patientId,
        start_date: dateRange.start_date,
        stop_date: dateRange.stop_date,
      });

      setData(response.data);
      setMetadata(response.metadata);
    } catch (err) {
      console.error("Error fetching body weight data:", err);
      setError("Failed to fetch body weight data");
      setData([]);
      setMetadata(null);
    } finally {
      setLoading(false);
    }
  }, [patientId, timeRange, navigationOffset, selectedDate]);

  useEffect(() => {
    if (patientId) {
      void fetchData();
    }
  }, [patientId, fetchData]);

  return {
    data,
    metadata,
    loading,
    error,
    refetch: fetchData,
  };
};
